var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Chip, Divider, Popper, chipClasses } from "@vividtheory/minerva-lab";
import { AddCircleOutline, ExpandMoreOutline, SearchOutline, } from "@vividtheory/minerva-lab/icons";
import { DataGridPro, GridColumnHeaders, gridClasses, } from "@vividtheory/minerva-legacy";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import { pipe } from "fp-ts/lib/function";
import { isEmpty, isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { Grid, ShrinkingField, Stack, Typography, CircularLoading, EmptyState, } from "../../../components";
import { DataGridChips } from "./DataGridChips";
import { DataGridPagination } from "./DataGridPagination";
export const DataGridView = (_a) => {
    var _b;
    var { rows, columns, enableColumnResize, handleDebounceOnSearch, handleFilterItemChange, handleLogicOperatorChange, colFilterItems, colLogicOperator, filterSearchPaginateProps, columnHeaderHeight = 44, density = "standard", rowHeight = density === "standard" ? 52 : density === "compact" ? 42 : 80, rowBuffer = 15, disableColumnMenu = true, hideFilterChips = false, slots, slotProps, sx, customEndElement, customNoRowsOverlay } = _a, props = __rest(_a, ["rows", "columns", "enableColumnResize", "handleDebounceOnSearch", "handleFilterItemChange", "handleLogicOperatorChange", "colFilterItems", "colLogicOperator", "filterSearchPaginateProps", "columnHeaderHeight", "density", "rowHeight", "rowBuffer", "disableColumnMenu", "hideFilterChips", "slots", "slotProps", "sx", "customEndElement", "customNoRowsOverlay"]);
    const filterableColumns = columns.filter((col) => {
        var _a;
        return (col.filterable || ((_a = props.filterableField) === null || _a === void 0 ? void 0 : _a.includes(col.field))) &&
            col.headerName;
    });
    const [chipState, setChipState] = useState([
        ...pipe(filterableColumns, A.map((item) => ({ id: item.headerName || "", open: false }))),
        ...pipe(filterableColumns, A.map((item) => ({ id: `${item.headerName}-1` || "", open: false }))),
        ...[
            { id: "Operator", open: false },
            { id: "Operator-1", open: false },
        ],
    ]);
    const toggleHeaderOpen = ({ id, open }) => {
        setChipState((prev) => pipe(prev, A.map((item) => isEqual(item.id, id) ? Object.assign(Object.assign({}, item), { open }) : item)));
    };
    const getChipState = (label) => {
        return pipe(chipState, A.findFirst((item) => isEqual(item.id, label)), O.foldW(() => undefined, (data) => data));
    };
    const { searchInputValue, setSearchInputValue } = filterSearchPaginateProps;
    useEffect(() => {
        handleDebounceOnSearch(searchInputValue === "" ? undefined : searchInputValue);
    }, [searchInputValue, handleDebounceOnSearch]);
    const noRow = isEmpty(filterSearchPaginateProps.searchText) &&
        isEmpty((_b = filterSearchPaginateProps.filterObject) === null || _b === void 0 ? void 0 : _b.filters) &&
        isEmpty(rows);
    return (React.createElement(Stack, { height: "100%", width: "100%" },
        React.createElement(Stack, { width: "100%", gap: 4, sx: {
                padding: 4,
                border: 1,
                borderColor: (theme) => theme.palette.stroke[100],
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
            } },
            React.createElement(Grid, { container: true, justifyContent: "space-between", alignItems: "center", gap: 2 },
                props.title && (React.createElement(Grid, { item: true, xs: true },
                    React.createElement(Typography, { variant: "body3", fontWeight: "semibold", truncate: true }, props.title))),
                !hideFilterChips && !props.title && (React.createElement(Grid, { item: true, xs: true, sx: {
                        opacity: noRow ? 0 : 1,
                    } },
                    React.createElement(DataGridChips, { filterableColumns: filterableColumns, getChipState: getChipState, handleFilterItemChange: handleFilterItemChange, toggleHeaderOpen: toggleHeaderOpen, colFilterItems: colFilterItems, colLogicOperator: colLogicOperator, handleLogicOperatorChange: handleLogicOperatorChange }))),
                !props.disableSearch && (React.createElement(Grid, { item: true, sx: {
                        overflow: "hidden",
                    } },
                    React.createElement(ShrinkingField, { placeholder: "Search", startNode: React.createElement(SearchOutline, null), value: searchInputValue, onChange: (_, v) => setSearchInputValue(v || ""), disabled: noRow }))),
                customEndElement && customEndElement),
            !hideFilterChips && props.title && !noRow && (React.createElement(DataGridChips, { filterableColumns: filterableColumns, getChipState: getChipState, handleFilterItemChange: handleFilterItemChange, toggleHeaderOpen: toggleHeaderOpen, colFilterItems: colFilterItems, colLogicOperator: colLogicOperator, handleLogicOperatorChange: handleLogicOperatorChange }))),
        React.createElement(DataGridPro, Object.assign({ rows: rows, rowHeight: rowHeight, columns: columns, columnHeaderHeight: columnHeaderHeight, rowBuffer: rowBuffer, disableColumnMenu: true, disableColumnReorder: true, disableColumnResize: !enableColumnResize, disableColumnSelector: true, disableRowSelectionOnClick: true, disableMultipleRowSelection: true, disableColumnFilter: true, disableChildrenSorting: true, disableMultipleColumnsSorting: true, filterMode: "server", paginationMode: "server", pagination: true, rowCount: 100 }, props, { slots: Object.assign({ columnHeaders: React.memo(GridColumnHeaders), loadingOverlay: () => (React.createElement(Stack, { width: "100%", height: "100%", justifyContent: "center", alignItems: "center" },
                    React.createElement(CircularLoading, { size: "xl" }))), noRowsOverlay: () => noRow && customNoRowsOverlay ? (
                //if no rows (no data and no filters and no search), show custom EmptyState component
                customNoRowsOverlay) : (
                //if no results (no search/filter results with applid filters or search), show default no results empty state
                React.createElement(EmptyState, { sx: {
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }, icon: React.createElement(SearchOutline, null), title: "No Results Found" })), pagination: () => (React.createElement(DataGridPagination, { currentPage: filterSearchPaginateProps.pageIndex, numberOfPages: Math.ceil((props.rowCount || 0) /
                        filterSearchPaginateProps.pageLimit), setPage: filterSearchPaginateProps.setPageIndex, showing: {
                        current: rows.length,
                        all: props.rowCount || 0,
                    } })) }, slots), slotProps: Object.assign({}, slotProps), sx: Object.assign(Object.assign(Object.assign({ minHeight: "220px", borderColor: (theme) => theme.palette.stroke[100], borderBottomLeftRadius: 4, borderBottomRightRadius: 4, 
                //Header
                [`.${gridClasses.columnHeader}`]: {
                    borderColor: (theme) => theme.palette.stroke[100],
                    "&:focus": {
                        outline: "none",
                    },
                    "&:focus-within": {
                        outline: "none",
                    },
                    backgroundColor: (theme) => theme.palette.surface.container,
                    color: (theme) => theme.palette.disabled.onContainer,
                    paddingX: 2,
                }, [`.${gridClasses.columnHeaderTitle}`]: {
                    fontFamily: (theme) => theme.typography.body3.fontFamily,
                    fontSize: (theme) => theme.typography.body3.fontSize,
                    fontWeight: (theme) => theme.typography.fontWeightSemiBold,
                }, [`.${gridClasses.pinnedColumnHeaders}`]: {
                    backgroundColor: (theme) => theme.palette.background.container,
                    color: (theme) => theme.palette.background.onContainer,
                }, [`.${gridClasses.pinnedColumns}`]: {
                    backgroundColor: (theme) => theme.palette.background.container,
                    color: (theme) => theme.palette.background.onContainer,
                }, 
                // Remove Mui applied border radius
                "--unstable_DataGrid-radius": 0, 
                // Cell
                [`.${gridClasses.cell}`]: {
                    paddingX: 2,
                    "&:focus": {
                        outline: "none",
                    },
                    "&:focus-within": {
                        outline: "none",
                    },
                    minHeight: 100,
                } }, (!enableColumnResize && {
                [`.${gridClasses.columnSeparator}`]: {
                    display: "none",
                },
            })), { 
                //Row
                [`.${gridClasses.row}`]: {
                    "&.Mui-selected": {
                        backgroundColor: (theme) => `${theme.palette.disabled.container} !important`,
                    },
                }, [`.${gridClasses.withBorderColor}`]: {
                    borderColor: (theme) => theme.palette.stroke[100],
                }, 
                // Footer
                [`.${gridClasses.footerContainer}`]: {
                    minHeight: "unset",
                } }), sx) }))));
};
export const FilterChipPopper = ({ content, chipLabel, open, setOpen, }) => {
    return (React.createElement(Popper, { placement: "bottom-start", content: content, open: open, onOpenChange: (v) => {
            setOpen === null || setOpen === void 0 ? void 0 : setOpen({ id: chipLabel || "", open: v });
        } },
        React.createElement(Chip, { density: "dense", variant: "outlined", startNode: React.createElement(AddCircleOutline, { size: "sm" }), sx: {
                [`&.${chipClasses.outlined}`]: {
                    boxShadow: "none",
                },
                border: 1,
                borderStyle: "dashed",
                borderColor: (theme) => theme.palette.outline,
                borderRadius: (theme) => theme.radius.full,
            } }, chipLabel)));
};
export const FilterTag = ({ startNode, field, operator, content, open, setOpen, }) => {
    return (React.createElement(Popper, { content: content, placement: "bottom-start", open: open, onOpenChange: (v) => {
            setOpen === null || setOpen === void 0 ? void 0 : setOpen({ id: `${field}-1` || "", open: v });
        } },
        React.createElement(Chip, { startNode: startNode, density: "dense", sx: { borderRadius: (theme) => theme.radius.full }, endNode: React.createElement(ExpandMoreOutline, { size: "xs", onChange: () => {
                    setOpen === null || setOpen === void 0 ? void 0 : setOpen({ id: `${field}-1` || "", open: !open });
                } }) },
            React.createElement(Grid, { container: true, sx: { gap: 3, flexWrap: "nowrap" } },
                React.createElement(Typography, { sx: { display: "inline", textWrap: "nowrap" }, variant: "body3", fontWeight: "semibold" }, field),
                React.createElement(Divider, { orientation: "vertical" }),
                React.createElement(Typography, { sx: { display: "inline", textWrap: "nowrap" }, variant: "body3", fontWeight: "semibold" }, operator)))));
};
//# sourceMappingURL=DataGridView.js.map